import { anyDone, applyToggle, canSelfComplete, relevantParticipants } from "./completion.js";
// `pid` is ignored for a `shared` item — the box is a single collapsed record (anyDone),
// same scoping applyToggle already uses for its shared branch.
export const packingStateOf = (item, pid, stagingEnabled) => {
    const isShared = item.assignment.kind === "shared";
    const packed = isShared ? anyDone(item, "completions") : (item.completions.byParticipant[pid]?.done ?? false);
    if (packed)
        return "packed";
    if (stagingEnabled) {
        const staged = isShared ? anyDone(item, "staging") : (item.staging?.byParticipant[pid]?.done ?? false);
        if (staged)
            return "staged";
    }
    return "unstaged";
};
// The row's displayed state for an `everyone`/`specific` item (T27 §5.3): worst-case
// collapse across every relevant participant — packed only if ALL are packed, staged if
// ALL are at least staged, else unstaged. A single click still only advances the actor's
// own entry (see `advanceStagingState`); this is purely what the row shows.
export const rowPackingState = (item, list, stagingEnabled) => {
    const participants = relevantParticipants(item, list);
    if (item.assignment.kind === "shared") {
        return packingStateOf(item, participants[0], stagingEnabled);
    }
    if (participants.length === 0)
        return "unstaged";
    const states = participants.map((pid) => packingStateOf(item, pid, stagingEnabled));
    if (states.every((s) => s === "packed"))
        return "packed";
    if (states.every((s) => s !== "unstaged"))
        return "staged";
    return "unstaged";
};
// Which of a row's relevant participants THIS actor can actually move (issue #203's
// item-level "check all" needs this — themself, any managed profile, or — with
// allowProxyCompletion on — everyone). A real co-traveler's entry only ever advances via
// their own pill otherwise, so an aggregate over every relevant participant would sit
// stuck at "unstaged" forever once the actor (and any managed profiles) reach the top.
export const affectableParticipants = (item, list, actor) => relevantParticipants(item, list).filter((pid) => {
    const member = list.members[pid];
    return member?.kind === "managed" || pid === actor || list.settings.allowProxyCompletion;
});
// rowPackingState's aggregate, scoped to affectableParticipants instead of every relevant
// participant — the item-level "check all" control's own view of "what state is this row
// in, as far as I can move it" (display and the next-target calculation both use this).
// Not meaningful for a `shared` item (no per-participant slots to scope) — callers route
// those to the single-checkbox CompletionControl instead.
export const affectableRowPackingState = (item, list, actor, stagingEnabled) => {
    const participants = affectableParticipants(item, list, actor);
    if (participants.length === 0)
        return "unstaged";
    const states = participants.map((pid) => packingStateOf(item, pid, stagingEnabled));
    if (states.every((s) => s === "packed"))
        return "packed";
    if (states.every((s) => s !== "unstaged"))
        return "staged";
    return "unstaged";
};
// Trip-wide staging progress (T27 progress bar): one combined "packed > staged > empty"
// bar instead of two independent ones, so every packed slot also counts toward staged —
// packed already implies "at least staged" regardless of whether the staging map was
// ever written for that slot (e.g. a slot packed via a bulk "check all" that skips the
// staged step entirely). `packed` and `staged` are cumulative tiers: staged is the count
// that's staged-or-further, not staged-only, so a caller renders staged's remainder
// (staged - packed) as the middle segment.
export const computeStagingProgress = (items, list) => {
    let packed = 0;
    let staged = 0;
    let required = 0;
    for (const item of items) {
        const isShared = item.assignment.kind === "shared";
        const participants = isShared ? relevantParticipants(item, list).slice(0, 1) : relevantParticipants(item, list);
        required += isShared ? 1 : participants.length;
        for (const pid of participants) {
            const state = packingStateOf(item, pid, true);
            if (state === "packed")
                packed += 1;
            else if (state === "staged")
                staged += 1;
        }
    }
    return { packed, staged: staged + packed, required };
};
export const nextPackingState = (state, stagingEnabled) => {
    if (!stagingEnabled)
        return state === "packed" ? "unstaged" : "packed";
    switch (state) {
        case "unstaged": return "staged";
        case "staged": return "packed";
        case "packed": return "unstaged";
    }
};
// Advances `target`'s state one step along unstaged -> staged -> packed -> unstaged (per
// participant entry, per assignment.kind's existing fan-out — see applyToggle). Delegates
// the actual map mutation to applyToggle so shared/everyone/specific scoping and the
// proxy rule stay in one place.
export const advanceStagingState = (item, actor, target, targetIsUser, stagingEnabled, opts) => {
    const current = packingStateOf(item, target, stagingEnabled);
    const next = nextPackingState(current, stagingEnabled);
    if (next === "staged") {
        return applyToggle(item, actor, target, targetIsUser, { ...opts, field: "staging" });
    }
    if (next === "packed") {
        return applyToggle(item, actor, target, targetIsUser, { ...opts, field: "completions" });
    }
    // next === "unstaged": clear completions, then staging only if it was actually set
    // (stagingEnabled false, or a straight unstaged->packed skip, never touched it).
    const cleared = applyToggle(item, actor, target, targetIsUser, { ...opts, field: "completions" });
    if (!cleared.ok)
        return cleared;
    const isShared = item.assignment.kind === "shared";
    const stagingWasSet = isShared ? anyDone(item, "staging") : (item.staging?.byParticipant[target]?.done ?? false);
    if (!stagingWasSet)
        return cleared;
    return applyToggle(cleared.item, actor, target, targetIsUser, { ...opts, field: "staging" });
};
// Category-wide "check all" (T27 §1.6): bulk-advance the *current user's* own state, one
// step, on every item in the category they're assigned to (canSelfComplete) — same
// click-cycle semantics as advancing a single item. Items the actor isn't assigned to,
// or where the toggle is rejected (shouldn't happen for a self-toggle), are left as-is.
export const advanceCategoryStaging = (items, categoryId, list, actor, stagingEnabled, opts) => items.map((item) => {
    if (item.categoryId !== categoryId)
        return item;
    if (!canSelfComplete(item, list, actor))
        return item;
    const result = advanceStagingState(item, actor, actor, true, stagingEnabled, opts);
    return result.ok ? result.item : item;
});
//# sourceMappingURL=staging.js.map